/**
 * SidebarGroupHeader - Generic collapsible group header for sidebar sections
 *
 * A reusable component for displaying group headers with:
 * - Expand/collapse functionality
 * - Icon display
 * - Item count badge
 * - Optional hover actions (like "create folder" button)
 */

import React, { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronRight, FaPlus } from "react-icons/fa";

// ============================================================================
// Types
// ============================================================================

export interface SidebarGroupHeaderProps {
  /** Group display name */
  groupKey: string;
  /** Icon to display */
  icon: React.ReactNode;
  /** Number of items in the group */
  count: number;
  /** Whether the group is expanded */
  isExpanded: boolean;
  /** Callback when expand/collapse is toggled */
  onToggle: () => void;
  /** Optional callback to create a new folder/subgroup */
  onCreateFolder?: () => void;
  /** Whether to show the create folder button */
  showCreateFolder?: boolean;
  /** Additional hover actions */
  hoverActions?: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

const SidebarGroupHeader: React.FC<SidebarGroupHeaderProps> = ({
  groupKey,
  icon,
  count,
  isExpanded,
  onToggle,
  onCreateFolder,
  showCreateFolder = true,
  hoverActions,
  className = "",
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleCreateFolder = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onCreateFolder?.();
    },
    [onCreateFolder]
  );

  return (
    <motion.div
      whileHover={{ x: 2 }}
      onClick={onToggle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-[var(--arc-sidebar-accent-glow)] transition-all text-[var(--arc-sidebar-text-secondary)] hover:text-[var(--arc-sidebar-text-primary)] ${className}`}
    >
      {/* Expand/Collapse Icon */}
      <motion.span
        animate={{ rotate: isExpanded ? 90 : 0 }}
        transition={{ duration: 0.2 }}
        className="w-4 flex-shrink-0"
      >
        <FaChevronRight size={10} />
      </motion.span>

      {/* Group Icon */}
      <span className="flex-shrink-0 w-4 h-4 flex items-center justify-center">
        {icon}
      </span>

      {/* Group Name */}
      <span className="flex-1 text-[var(--arc-sidebar-text-primary)] text-sm font-medium truncate">
        {groupKey || "Sans groupe"}
      </span>

      {/* Hover Actions */}
      <AnimatePresence>
        {isHovered && (
          <motion.div
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 10 }}
            transition={{ duration: 0.15 }}
            className="flex items-center gap-1"
          >
            {showCreateFolder && onCreateFolder && (
              <button
                onClick={handleCreateFolder}
                className="p-1 rounded hover:bg-[var(--arc-sidebar-accent-glow)] hover:text-[var(--arc-sidebar-text-primary)] transition-colors"
                title="Créer un dossier"
              >
                <FaPlus size={10} />
              </button>
            )}
            {hoverActions}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Count Badge */}
      {!isHovered && count > 0 && (
        <span className="text-xs text-[var(--arc-sidebar-text-secondary)]">
          {count}
        </span>
      )}
    </motion.div>
  );
};

SidebarGroupHeader.displayName = "SidebarGroupHeader";

export default SidebarGroupHeader;

// ============================================================================
// Group Content Wrapper
// ============================================================================

export interface SidebarGroupContentProps {
  /** Whether the group is expanded */
  isExpanded: boolean;
  /** Child content */
  children: React.ReactNode;
  /** Message to show when empty */
  emptyMessage?: string;
  /** Whether the group is empty */
  isEmpty?: boolean;
}

/**
 * SidebarGroupContent - Animated wrapper for group content
 */
export const SidebarGroupContent: React.FC<SidebarGroupContentProps> = ({
  isExpanded,
  children,
  emptyMessage = "Aucun élément",
  isEmpty = false,
}) => {
  return (
    <AnimatePresence>
      {isExpanded && !isEmpty && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="overflow-hidden"
        >
          <div className="ml-4 border-l border-[var(--arc-sidebar-border-accent)] pl-2 py-1 space-y-0.5">
            {children}
          </div>
        </motion.div>
      )}

      {isExpanded && isEmpty && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="overflow-hidden"
        >
          <div className="ml-6 pl-2 py-2 text-[var(--arc-sidebar-text-secondary)]">
            <span className="text-xs italic">{emptyMessage}</span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

SidebarGroupContent.displayName = "SidebarGroupContent";
